// #region: Class definition *********************************************************************/
/**
 * Broadcast predefined events for subscribers, @see {broadcasts}.
 */
window.eventBroadcaster = new class {

    // #region: Private Fields *******************************************************************/
    #broadcasts;
    #windowBroadcasts;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * The constructor does work that needs to be executed exactly once.
     */
    constructor() {
        /** 
         * Stores the broadcasts and its corresponding listeners/subscribers.
         * @type {Map}
         */
        this.#broadcasts = new Map();

        /**
         * Stores the window broadcasts and its corresponding listeners/subscribers.
         * We use a separate map as window events typically only fire once.
         * @type {Map}
         */
        this.#windowBroadcasts = new Map();
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {String}                        The string representation of this class.
     */
    toString() {
        return "[eventBroadcaster]";
    }

    static toString() {
        return "[eventBroadcaster]";
    }

    /** 
     * Identifies the version.
     * @returns {String}                        The version number of this class.
     */
    static version() {
        return "1.0.0";
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Broadcast message to subscribers.
     * @param {Object} [detail=null]            Optional and defaulting to null, of type any, that is an event-dependent value associated with the event.
     * @param {String} message                  The (predefined) message to broadcast @see {broadcasts}.
     * @param {Boolean} isWindowMessage         Optional and defaulting to false, of type boolean, that indicates if it is a window event.
     */
    broadcast({detail = null, isWindowMessage = false, message}) {
        if(message == null) {
            console.log("Undefined broadcast.");
        }
        else {
            const list = [];
            const subscribers = isWindowMessage ? this.#windowBroadcasts.get(message) : this.#broadcasts.get(message);
            let i = 0;

            if(subscribers != null) {
                // This will store possible failed subscribers.
                const exceptions = [];

                // First collect in a temp list. This will prevent sudden unsubscribers from modifying the length of the list while we iterate.
                while(i < subscribers.length) {
                    list.push(subscribers[i++]);
                }

                i = 0;
                while(i < list.length) {
                    const subscriber = list[i];
                    try {
                        if (subscriber.handleBroadcast) {
                            subscriber.handleBroadcast({ detail, message });
                        }
                        else if (typeof (subscriber) === "function") {
                            subscriber({ detail, message });
                        }
                        else {
                            console.error(`${subscriber.toString()} has no method 'handleBroadcast' or is not a Function.\nBroadcast cannot be evaluated.`);
                        }

                    }
                    catch (exception) {
                        exceptions.push(subscriber);
                        if (window.isDebugMode) {
                            debugger;
                        }

                        console.error(`Exception in ${ subscriber.toString() } on broadcast ${ message }.\n${ exception.message ?? exception }\nStack:\n${ exception.stack ?? "" }`);
                    }

                    // Window events should fire only once.
                    if(isWindowMessage) {
                        eventBroadcaster.unsubscribe({message, subscriber});
                    }

                    i += 1;
                }

                // Brutally exclude subscribers that raised exceptions.
                exceptions.forEach((subscriber) => {
                    eventBroadcaster.unsubscribe({message, subscriber});
                });
            }
        }
    }

    /**
     * Message has subscribers?
     * @param {String} message                  The (predefined) message (@see {broadcasts}) to verify if it has a subscriber.
     * @returns {Boolean}                       True if any subscribers exist, false if not.
     */
    hasSubscribers({message}) {
        return this.#broadcasts.get(message)?.length > 0;
    }

    /**
     * Add subscription. Should implement method handleBroadcast.
     * @param {String} message                  The (predefined) message @see {broadcasts}.
     * @param {Element} subscriber              The subscribing element/class/document/window.
     */
    subscribe({message, subscriber}) {
        if(message == null) {
            console.log(`Exception in '${ subscriber.toString() }' on broadcast '${ message }'`);

            return;
        }

        // Normal or window message?
        const map = message.startsWith("window") ? this.#windowBroadcasts : this.#broadcasts;
        if(map.has(message)) {
            const messageArray = map.get(message);
            messageArray.push(subscriber);
            map.set(message, messageArray);
        }
        else {
            map.set(message, [subscriber]);
        }
    }

    /**
     * Remove subscription.
     * @param {String} message                  The (predefined) message @see {broadcasts}.
     * @param {Element} subscriber              The element/class/document/window that should be removed from the listeners list.
     */
    unsubscribe({message, subscriber}) {
        if(message == null) {
            if (window.isDebugMode) {
                debugger;
            }

            throw new Error(`Undefined broadcast: ${ unsubscriber }`);
        }

        // Normal or window message?
        const isWindowMessage = message.startsWith("window");
        const map = isWindowMessage ? this.#windowBroadcasts : this.#broadcasts;
        const subscribers = map.get(message);
        let i = 0;

        if(subscribers) {
            while(i < subscribers.length) {
                const unsubscriber = subscribers[i];
                if(subscriber === unsubscriber) {
                    subscribers.splice(i, 1);
                    break;
                }

                i++;
            }
        }

        // No more subscribers, then remove the item completely.
        if(subscribers == null || subscribers?.length === 0) {
            map.delete(message);
        }
    }
    // #endregion ********************************************************************************/

}();
// #endregion ************************************************************************************/
